import EmailProgramSelector from './EmailProgramSelector';
import RecipientAutocomplete from './RecipientAutocomplete';
import RecipientFilterSummary from './RecipientFilterSummary';
import EmailComposerDialogShell from '@/components/email-composer/EmailComposerDialogShell';
import { useEmailComposer } from '@/hooks/use-email-composer';
import type { EmailComposerMode } from '@/hooks/use-email-composer-mode';
import { BULK_AFFILIATE_EMAIL_LIMIT } from '@fluenceur/types';
import useAffiliateBulkEmailStore, {
  type RecipientData,
} from '@/stores/affiliate-bulk-email-store';
import type { BulkAffiliateEmailFilters } from '@fluenceur/types';
import { Box, Text } from '@chakra-ui/react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface BulkAffiliateEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  programId?: string;
  affiliates?: Pick<RecipientData, 'id' | 'name' | 'avatarUrl' | 'email' | 'programs'>[];
  /** Current table filters, used to resolve recipients in "select all matching" mode. */
  filters?: BulkAffiliateEmailFilters;
  /**
   * A message the opening surface pre-fills, read once on mount. Omitted by the
   * table flows, where the brand writes from scratch.
   */
  draft?: { subject: string; body: string } | null;
  /** Gift outreach receives a frozen target snapshot: recipients cannot drift in the composer. */
  mode?: EmailComposerMode;
  /** Deduplicated recipient exclusions from the server-owned gift snapshot. */
  giftExclusions?: Array<{ recipientId: string | null; reason: string }>;
}

const BulkAffiliateEmailModal: React.FC<BulkAffiliateEmailModalProps> = ({
  isOpen,
  onClose,
  programId,
  affiliates = [],
  filters,
  draft,
  mode,
  giftExclusions = [],
}) => {
  const { t } = useTranslation();
  const storeProgramId = useAffiliateBulkEmailStore((s) => s.programId);

  // Seed store when opened from table flow
  const hasSeeded = useRef(false);
  useEffect(() => {
    if (isOpen && !hasSeeded.current) {
      hasSeeded.current = true;
      const store = useAffiliateBulkEmailStore.getState();
      // In "select all matching" mode the cohort is resolved server-side, so we
      // don't seed (or clobber) individual recipients.
      if (!store.selectAllMatching && affiliates.length > 0) {
        const currentIds = store.selectedIds;
        store.setRecipients(
          affiliates
            .filter((a) => currentIds.has(a.id))
            .map((a) => ({
              id: a.id,
              name: a.name,
              email: a.email ?? '',
              avatarUrl: a.avatarUrl,
              programs: a.programs ?? [],
            }))
        );
      }
      if (programId) {
        store.setProgramId(programId);
      }
    }
    if (!isOpen) {
      hasSeeded.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const composer = useEmailComposer({
    hasProgramContext: !!storeProgramId,
    mode,
    selectAllMatchingFilters: filters,
    initialDraft: draft,
    onClose,
  });

  const recipientSlot =
    mode?.type === 'gift-bulk' ? (
      <Box>
        <RecipientFilterSummary
          count={composer.recipientCount}
          summaryKey="gifting.tracking.bulk.emailRecipients"
        />
        {composer.recipientCount === 0 ? (
          <Text mt="1" fontSize="sm" color="fg.error">
            {t('gifting.tracking.bulk.noEmailableRecipients')}
          </Text>
        ) : composer.recipientCount > BULK_AFFILIATE_EMAIL_LIMIT ? (
          <Text mt="1" fontSize="sm" color="fg.error">
            {t('gifting.tracking.bulk.emailLimitExceeded', {
              limit: BULK_AFFILIATE_EMAIL_LIMIT,
            })}
          </Text>
        ) : null}
        {giftExclusions.length > 0 ? (
          <Box mt="1" fontSize="xs" color="fg.muted">
            <Text>
              {t('gifting.tracking.bulk.emailExcluded', {
                count: giftExclusions.length,
              })}
            </Text>
            {Array.from(new Set(giftExclusions.map((exclusion) => exclusion.reason))).map(
              (reason) => (
                <Text key={reason}>
                  {t(`gifting.tracking.bulk.emailExclusionReasons.${reason}`, {
                    defaultValue: reason,
                  })}
                </Text>
              )
            )}
          </Box>
        ) : null}
      </Box>
    ) : composer.selectAllMatching ? (
      <RecipientFilterSummary count={composer.recipientCount} />
    ) : (
      <RecipientAutocomplete variant="row" />
    );

  return (
    <EmailComposerDialogShell
      composer={composer}
      isOpen={isOpen}
      title={t('bulkEmail.compose.title')}
      showComposer={composer.isConnected}
      labels={{
        backToEdit: t('bulkEmail.compose.confirm.backToEdit'),
        send: t('bulkEmail.compose.send'),
        confirmTitle: t('bulkEmail.compose.confirm.title'),
        confirmDescription: t('bulkEmail.compose.confirm.description', {
          count: composer.recipientCount,
          email: composer.senderEmail,
        }),
        confirmSend: t('bulkEmail.compose.confirm.send', {
          count: composer.recipientCount,
        }),
      }}
      recipientSlot={recipientSlot}
      programSlot={
        mode?.type === 'gift-bulk' ? null : (
          <EmailProgramSelector locked={!!programId} variant="row" />
        )
      }
    />
  );
};

export default BulkAffiliateEmailModal;
